import type React from 'react'
import {useEffect} from 'react'
import {useOktaAuth} from '@okta/okta-react'
import {useNavigate, useSearchParams} from 'react-router-dom'
import {clearOktaTokens} from '@/utils/authUtils'
import LoadingSpinner from '@/components/GDS/atoms/LoadingSpinner'

/**
 * Login component that redirects to Okta login
 */
const Login: React.FC = () => {
  const {oktaAuth, authState} = useOktaAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  useEffect(() => {
    const handleAuth = async () => {
      if (authState?.isAuthenticated === undefined) {
        // Still loading auth state
        return
      }

      if (!authState?.isAuthenticated) {
        try {
          // Clear all previous Okta tokens to ensure clean login state
          clearOktaTokens()

          // Check if we need to force login prompt (from logout flow)
          const forcePrompt =
            searchParams.get('prompt') === 'login' || sessionStorage.getItem('justLoggedOut') === 'true'

          // Clear the logout flag
          if (sessionStorage.getItem('justLoggedOut')) {
            sessionStorage.removeItem('justLoggedOut')
          }

          // Get the return URL from sessionStorage (set by ProtectedRoute)
          const returnUrl = sessionStorage.getItem('returnUrl') || '/'

          // Clear the return URL from storage
          sessionStorage.removeItem('returnUrl')

          // Redirect to Okta login with optional prompt parameter
          await oktaAuth.signInWithRedirect({
            originalUri: returnUrl,
            // Force login prompt if coming from logout
            ...(forcePrompt && {
              extraParams: {
                prompt: 'login',
              },
            }),
          })
        } catch (error) {
          console.error('Error during sign-in redirect:', error)
        }
      } else {
        // If authenticated, check for return URL
        const returnUrl = sessionStorage.getItem('returnUrl')
        if (returnUrl) {
          sessionStorage.removeItem('returnUrl')
          navigate(returnUrl, {replace: true})
        } else {
          navigate('/') // Redirect to Dashboard if authenticated
        }
      }
    }

    handleAuth()
  }, [authState?.isAuthenticated, oktaAuth, navigate, searchParams])

  return <LoadingSpinner />
}

export default Login
